import { Component, computed, inject, signal } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { Store } from '@ngxs/store';
import { VxButtonComponent } from '@ui/button/button.component';
import { VxCardComponent } from '@ui/card/card.component';
import { VxCardFooterComponent } from '@ui/card/card-footer.component';
import { VxFormFieldComponent } from '@ui/form-field/form-field.component';
import { VxIconComponent } from '@ui/icon/icon.component';
import { VxInputComponent } from '@ui/input/input.component';
import { VxPanelComponent } from '@ui/panel/panel.component';
import { VxTooltipDirective } from '@ui/tooltip/tooltip.directive';
import { SharedModule } from 'src/app/core/shared.module';
import { HasQuota } from 'src/app/helpers/quota';
import { CreatePageComponent } from 'src/app/layout/create-page/create-page.component';
import { CheckRoleDirective } from 'src/app/services/permission/permission.directive';
import { ToastService } from 'src/app/services/toast/toast.service';
import { TranslationService } from 'src/app/services/translation/translation.service';
import { CreateAlertProcessorAction } from 'src/app/store/alerts/alerts.actions';
import { AlertsState } from 'src/app/store/alerts/alerts.state';
import { SubscriptionState } from 'src/app/store/subscription/subscription.state';
import {
  buildAlertProcessorConfigurationForm,
  toAlertProcessorConfigurationSpec,
} from '../components/configuration-wizard/configuration-form';
import { AlertConfigurationWizardComponent } from '../components/configuration-wizard/configuration-wizard.component';

const DOCS_URL = 'https://docs.incidite.com/docs/alerting/';
const CONTACT_URL = 'https://incidite.com/contact';

@Component({
  selector: 'app-page-create-alert-processor',
  templateUrl: 'create.component.html',
  imports: [
    SharedModule,
    ReactiveFormsModule,
    RouterLink,
    CheckRoleDirective,
    CreatePageComponent,
    AlertConfigurationWizardComponent,
    VxButtonComponent,
    VxCardComponent,
    VxCardFooterComponent,
    VxFormFieldComponent,
    VxIconComponent,
    VxInputComponent,
    VxPanelComponent,
    VxTooltipDirective,
  ],
  host: { class: 'block' },
})
export class CreateAlertProcessorPageComponent {
  private store = inject(Store);
  private router = inject(Router);
  private translation = inject(TranslationService);
  private toast = inject(ToastService);

  protected readonly docsUrl = DOCS_URL;
  protected readonly contactUrl = CONTACT_URL;

  protected form = new FormGroup({
    name: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    configuration: buildAlertProcessorConfigurationForm(),
  });

  protected isSaving = signal(false);

  private processorsFeature = this.store.selectSignal(
    SubscriptionState.feature('alerting-processors')
  );

  protected hasQuota = computed(() => {
    const feature = this.processorsFeature();
    return feature ? HasQuota(feature) : false;
  });

  protected quotaTooltip = computed(() => {
    this.translation.currentLocale$();
    return this.hasQuota()
      ? undefined
      : this.translation.translateInstantKey('misc.quotaReached');
  });

  protected onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const name = this.form.controls.name.value.trim();

    this.isSaving.set(true);
    this.store
      .dispatch(
        new CreateAlertProcessorAction({
          name,
          configuration: toAlertProcessorConfigurationSpec(
            this.form.controls.configuration
          ),
        })
      )
      .subscribe({
        next: () => {
          const createdId = this.store.selectSnapshot(
            AlertsState.alertProcessors
          ).latestCreatedId;
          this.toast.showInfo(
            'pages.settings.alerting.processors.create.toasts.created.header',
            'pages.settings.alerting.processors.create.toasts.created.message',
            { name }
          );
          this.router.navigate(['/settings/alerting/processors', createdId]);
        },
        // the state already surfaces the error as a toast
        error: () => undefined,
      })
      .add(() => this.isSaving.set(false));
  }
}
